import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdGTranslate } from 'react-icons/md';
import { FaAngleDown, FaTimes, FaCheck } from 'react-icons/fa';
import { translate } from '../../../utils/Common';
import styles from './LanguageSelector.module.css';

const LanguageSelector = ({ variant = 'default', isOpen: externalIsOpen, onClose: externalOnClose }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState('en');
    const [isLoading, setIsLoading] = useState(false);
    const [rippleData, setRippleData] = useState({ x: 0, y: 0, show: false, cardIndex: -1 });
    const modalRef = useRef(null);
    const languageCardsRef = useRef([]);
    const isControlled = externalIsOpen !== undefined && externalOnClose !== undefined;

    useEffect(() => {
        const langStorage = localStorage.getItem("language");
        if (langStorage) {
            setCurrentLanguage(langStorage);
        } else {
            const browserLang = window.navigator.language?.split("-")[0];
            const defaultLang = ['en', 'es', 'pt'].includes(browserLang) ? browserLang : 'en';
            setCurrentLanguage(defaultLang);
            localStorage.setItem("language", defaultLang);
        }
    }, []);

    useEffect(() => {
        if (isControlled) {
            setIsModalOpen(externalIsOpen);
        }
    }, [externalIsOpen, isControlled]);

    // Keyboard navigation
    useEffect(() => {
        if (!modalRef.current || !isModalOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            } else if (e.key === 'Tab') {
                e.preventDefault();
                const focusableElements = modalRef.current.querySelectorAll(
                    'button, [tabindex]:not([tabindex="-1"])'
                );
                const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
                const nextIndex = e.shiftKey 
                    ? (currentIndex - 1 + focusableElements.length) % focusableElements.length
                    : (currentIndex + 1) % focusableElements.length;
                focusableElements[nextIndex]?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen]);

    // Focus first element when modal opens
    useEffect(() => {
        if (isModalOpen && modalRef.current) {
            const firstButton = modalRef.current.querySelector('button');
            setTimeout(() => firstButton?.focus(), 100);
        }
    }, [isModalOpen]);

    const handleOpenModal = () => {
        if (!isControlled) {
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = useCallback(() => {
        if (isControlled) {
            externalOnClose();
        } else {
            setIsModalOpen(false);
        }
    }, [isControlled, externalOnClose]);

    const createRipple = (e, element, cardIndex) => {
        const rect = element.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setRippleData({ x, y, show: true, cardIndex });
        setTimeout(() => setRippleData({ x: 0, y: 0, show: false, cardIndex: -1 }), 600);
    };

    const handleSelectLanguage = async (lang, e, cardIndex) => {
        if (e) {
            createRipple(e, e.currentTarget, cardIndex);
        }

        if (lang !== currentLanguage) {
            setIsLoading(true);
            localStorage.setItem("language", lang);
            setCurrentLanguage(lang);
            
            // Small delay for smooth transition
            await new Promise(resolve => setTimeout(resolve, 300));
            handleCloseModal();
            
            // Reload after a brief moment
            setTimeout(() => {
                window.location.reload();
            }, 100);
        } else {
            handleCloseModal();
        }
    };

    const handleCardKeyDown = (e, lang, cardIndex) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectLanguage(lang, e, cardIndex);
        }
    };

    const languages = [
        { code: 'en', name: translate("translate.en"), flag: '/img/usa_flag.png' },
        { code: 'es', name: translate("translate.es"), flag: '/img/es_flag.png' },
        { code: 'pt', name: translate("translate.pt"), flag: '/img/br_flag.png' }
    ];

    const buttonClass = variant === 'navbar' 
        ? styles.buttonNavbar 
        : variant === 'login' 
        ? styles.buttonLogin 
        : variant === 'menu'
        ? styles.buttonMenu
        : styles.buttonDefault;

    const modalIsOpen = isControlled ? externalIsOpen : isModalOpen;

    return (
        <>
            {variant !== 'menu' && (
                <button 
                    className={buttonClass}
                    onClick={handleOpenModal}
                    aria-label={translate("bottomNav.language")}
                    type="button"
                >
                    <MdGTranslate className={styles.icon} />
                    <span className={styles.languageCode}>{currentLanguage.toUpperCase()}</span>
                    <FaAngleDown className={styles.arrow} />
                </button>
            )}

            {modalIsOpen && (
                <div 
                    className={styles.modalOverlay} 
                    onClick={handleCloseModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="language-modal-title"
                >
                    <div 
                        ref={modalRef}
                        className={styles.modalContent} 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            className={styles.modalCloseButton}
                            onClick={handleCloseModal}
                            aria-label="Close"
                            type="button"
                            tabIndex={0}
                        >
                            <FaTimes size={14} strokeWidth={1.5} />
                        </button>
                        
                        <h3 
                            id="language-modal-title"
                            className={styles.modalTitle}
                        >
                            {translate("translate.title")}
                        </h3>
                        
                        {isLoading && (
                            <div className={styles.loadingOverlay}>
                                <div className={styles.loadingSpinner} />
                            </div>
                        )}
                        
                        <div className={styles.languagesContainer}>
                            {languages.map((lang, index) => (
                                <div
                                    key={lang.code}
                                    ref={el => languageCardsRef.current[index] = el}
                                    className={`${styles.languageCard} ${
                                        currentLanguage === lang.code ? styles.languageCardActive : ''
                                    }`}
                                    onClick={(e) => handleSelectLanguage(lang.code, e, index)}
                                    onKeyDown={(e) => handleCardKeyDown(e, lang.code, index)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${lang.name}${currentLanguage === lang.code ? ", selected" : ""}`}
                                    aria-pressed={currentLanguage === lang.code}
                                    style={{
                                        animationDelay: `${index * 0.1}s`
                                    }}
                                >
                                    {rippleData.show && rippleData.cardIndex === index && (
                                        <span 
                                            className={styles.ripple}
                                            style={{
                                                left: rippleData.x,
                                                top: rippleData.y
                                            }}
                                        />
                                    )}
                                    
                                    {currentLanguage === lang.code && (
                                        <div className={styles.checkmarkContainer}>
                                            <FaCheck className={styles.checkmark} size={14} />
                                        </div>
                                    )}
                                    
                                    <div
                                        className={styles.flag}
                                        style={{
                                            backgroundImage: `url(${lang.flag})`,
                                            backgroundSize: 'contain',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat'
                                        }}
                                        aria-hidden="true"
                                    />
                                    <span className={styles.languageName}>{lang.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LanguageSelector;

